"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.localesToStringArr = exports.getLanguageCode = exports.getServerLocale = exports.getLocaleFromCookie = exports.getLocaleFromAcceptLang = exports.getLocale = exports.formatLocale = exports.SC_COOKIE_KEY_LOCALE = void 0;
/**
 * Adopt from angular translate http://rawgit.com/angular-translate/bower-angular-translate/master/angular-translate.js
 * Get the first browser language.
 * This could return a language code (en) or locale code (en-US/en_US/en-us).
 *
 * Why don't we use navigator.language directly? (https://stackoverflow.com/a/4079798/9634568)
 *
 * window.navigator.language (Chrome/FF/Safari) returns always browser language and not browser's
 * preferred language, but: "it's pretty common for English speakers (gb, au, nz, etc) to have an
 * en-us version of Firefox/Chrome/Safari." Hence window.navigator.language will still return en-US
 * even if the user preferred language is en-GB.
 */
const accept_language_parser_1 = __importDefault(require("accept-language-parser"));
const js_cookie_1 = __importDefault(require("js-cookie"));
const cookie_1 = __importDefault(require("cookie"));
const constants_1 = require("./constants");
exports.SC_COOKIE_KEY_LOCALE = "sc-language";
const getLocaleFromQuery = () => {
    if (typeof window === "undefined") {
        return null;
    }
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("locale");
};
const getFirstBrowserLanguage = () => {
    if (typeof window === "undefined") {
        return null;
    }
    const nav = window.navigator;
    const browserLanguagePropertyKeys = [
        "language",
        "browserLanguage",
        "systemLanguage",
        "userLanguage",
    ];
    let i;
    let language;
    // support for HTML 5.1 "navigator.languages"
    if (Array.isArray(nav.languages)) {
        for (i = 0; i < nav.languages.length; i++) {
            language = nav.languages[i];
            if (language?.length) {
                return language;
            }
        }
    }
    // support for other well known properties in browsers
    for (i = 0; i < browserLanguagePropertyKeys.length; i++) {
        // @ts-ignore
        language = nav[browserLanguagePropertyKeys[i]];
        if (language?.length) {
            return language;
        }
    }
    return null;
};
const LOCALE_SEPARATOR = /[-_]/;
/**
 * Derive the locale by checking various sources in order of preference
 * 1. Props (passed in here as a param)
 * 2. locale query parameter
 * 3. Cookie
 * 4. First browser language
 * 5. Default locale
 * @param locale Optional locale set from within a component
 * @returns The derived locale
 *
 * For Prioritization of Locales:
 * https://docs.google.com/document/d/1aN4J7Xxaw4JVV6LOcwOkrRBRHWWH6wOHO9JMAgMskGc/edit#
 *
 */
function deriveLocale(locale) {
    if (locale) {
        return locale;
    }
    const queryLanguage = getLocaleFromQuery();
    if (queryLanguage) {
        return queryLanguage;
    }
    const scLanguage = js_cookie_1.default.get(exports.SC_COOKIE_KEY_LOCALE);
    if (scLanguage) {
        return scLanguage;
    }
    const browserLanguage = getFirstBrowserLanguage();
    if (browserLanguage) {
        return browserLanguage;
    }
    return constants_1.DEFAULT_LOCALE;
}
const checkNonStandardizedLocaleCodes = (nonStandardLocale) => {
    for (const prospectiveLocale in constants_1.ALTERNATE_LOCALE_CODES) {
        const alternateFound = Boolean(constants_1.ALTERNATE_LOCALE_CODES[prospectiveLocale]?.find((alternateLocale) => nonStandardLocale.toLowerCase() === alternateLocale.toLowerCase()));
        if (alternateFound) {
            return prospectiveLocale;
        }
    }
};
const formatLocale = (locale) => {
    // countryCode can be lower case in some safari browsers
    const [lang, countryCode] = locale.split(LOCALE_SEPARATOR);
    const localeCode = countryCode ? `${lang}-${countryCode.toUpperCase()}` : lang;
    let maybeSupportedLocale = constants_1.ALL_LOCALES.find((supportedLocaleCode) => supportedLocaleCode.toLowerCase() === localeCode.toLowerCase());
    if (maybeSupportedLocale) {
        return maybeSupportedLocale;
    }
    maybeSupportedLocale = checkNonStandardizedLocaleCodes(localeCode);
    if (maybeSupportedLocale) {
        return maybeSupportedLocale;
    }
    // fallback to one locale, if we don't support this locale variant (e.g., en-XX) but we do support
    // one variant for this language, and fall back to the default one (e.g., en-US)
    // @ts-ignore
    if (lang in constants_1.LanguageCode) {
        return constants_1.LANGUAGE_TO_DEFAULT_LOCALE[lang];
    }
    return null;
};
exports.formatLocale = formatLocale;
/**
 * Determine the current user locale and return it as an enum
 * @param localeProp Optional locale set from within a component
 * @returns The current Locale
 */
const getLocale = (localeProp) => {
    const derivedLocale = deriveLocale(localeProp);
    return (0, exports.formatLocale)(derivedLocale) || constants_1.DEFAULT_LOCALE;
};
exports.getLocale = getLocale;
const getLocaleFromAcceptLang = (acceptLangHeader) => {
    if (!acceptLangHeader) {
        return null;
    }
    const acceptLangs = accept_language_parser_1.default.parse(acceptLangHeader);
    for (const acceptLang of acceptLangs) {
        const { code, region } = acceptLang;
        const localeCode = region ? `${code}-${region}` : code;
        if (Object.values(constants_1.Locale).includes(localeCode)) {
            return localeCode;
        }
        const maybeSupportedLocale = checkNonStandardizedLocaleCodes(localeCode);
        if (maybeSupportedLocale) {
            return maybeSupportedLocale;
        }
        if (Object.keys(constants_1.LanguageCode).includes(acceptLang.code)) {
            return constants_1.LANGUAGE_TO_DEFAULT_LOCALE[acceptLang.code];
        }
    }
    return null;
};
exports.getLocaleFromAcceptLang = getLocaleFromAcceptLang;
const getLocaleFromCookie = (cookies) => {
    const parsedCookie = cookie_1.default.parse(cookies);
    return parsedCookie[exports.SC_COOKIE_KEY_LOCALE]
        ? (0, exports.formatLocale)(parsedCookie[exports.SC_COOKIE_KEY_LOCALE])
        : undefined;
};
exports.getLocaleFromCookie = getLocaleFromCookie;
/**
 * Determine the current user locale based on incoming request in SSR
 * and return it as an enum
 * @param req Optional Next.js incoming request
 * @param path Optional Next.js URL path from router
 * @returns The current Locale or null, in which case the server provided locale
 * will be used
 *
 * For Prioritization of Locales:
 * https://docs.google.com/document/d/1aN4J7Xxaw4JVV6LOcwOkrRBRHWWH6wOHO9JMAgMskGc/edit#
 */
const getServerLocale = (req, path) => {
    if (!req && !path) {
        return null;
    }
    const localeParam = new URLSearchParams((req?.url ?? path)?.replace(/^\/.*\?/g, "")).get("locale");
    if (localeParam) {
        return (0, exports.formatLocale)(localeParam);
    }
    const localeFromCookies = (0, exports.getLocaleFromCookie)(req?.headers.cookie || "");
    if (localeFromCookies) {
        return (0, exports.formatLocale)(localeFromCookies);
    }
    const reqAcceptLangLocale = (0, exports.getLocaleFromAcceptLang)(req?.headers["accept-language"]);
    if (reqAcceptLangLocale) {
        return (0, exports.formatLocale)(reqAcceptLangLocale);
    }
    return null;
};
exports.getServerLocale = getServerLocale;
/**
 * Get language code to use, e.g., "en", "fr"
 */
const getLanguageCode = (locale = (0, exports.getLocale)()) => {
    const [lang] = locale.split(LOCALE_SEPARATOR);
    return lang;
};
exports.getLanguageCode = getLanguageCode;
/**
 * Helper function to convert a Locale array to a string array.
 * @param locales Locale array e.g. Locale.enUS
 * @returns Locale string array e.g. "en-US"
 */
const localesToStringArr = (locales) => Object.values(locales).map((locale) => locale.toString());
exports.localesToStringArr = localesToStringArr;
//# sourceMappingURL=locales.js.map